import { onUnmounted } from "vue";

/**
 * Creates a safe timeout manager that auto-clears on unmount.
 * Automatically clears any pending timeout before setting a new one.
 *
 * @returns {{ set: Function, clear: Function }}
 *
 * @example
 * const timer = useSafeTimeout();
 * timer.set(() => console.log('delayed'), 1000);
 * timer.clear(); // Optional manual clear
 */
export function useSafeTimeout() {
  let timerId = null;

  const clear = () => {
    if (timerId !== null) {
      clearTimeout(timerId);
      timerId = null;
    }
  };

  const set = (callback, delay) => {
    clear();
    timerId = setTimeout(callback, delay);
  };

  onUnmounted(clear);

  return { set, clear };
}

/**
 * Manages multiple event listeners with bulk add/remove.
 * Automatically removes all listeners on unmount.
 *
 * @param {EventTarget} target - The event target (document, element, etc.)
 * @param {string[]} events - Array of event names
 * @param {Function} handler - Event handler function
 * @returns {{ add: Function, remove: Function }}
 *
 * @example
 * const listeners = useEventListeners(document, ['mousemove', 'keydown', 'click'], handleActivity);
 * listeners.add();    // Start listening
 * listeners.remove(); // Stop listening (also called on unmount)
 */
export function useEventListeners(target, events, handler) {
  let isListening = false;

  const add = () => {
    if (isListening) return;
    events.forEach((event) => target.addEventListener(event, handler));
    isListening = true;
  };

  const remove = () => {
    if (!isListening) return;
    events.forEach((event) => target.removeEventListener(event, handler));
    isListening = false;
  };

  onUnmounted(remove);

  return { add, remove };
}
